/**
 * Erreurs d'envoi vers le backend MySquash (validation, feedback, etc.).
 *
 * Objectif : séparer le message *technique* (HTTP 401, payload JSON, stack) du
 * message *clair* présenté à l'arbitre. On classe l'erreur en `kind`, et la
 * couche UI traduit ce `kind` via i18n. Le détail technique reste accessible
 * (panneau repliable) pour le support terrain, mais ne fuite jamais comme
 * message principal.
 *
 * Volontairement générique : tout envoi POST (résultats, feedback, fin de jeu)
 * peut produire un `SendError` et réutiliser le même rendu.
 */

/** Catégorie d'erreur — mappée 1:1 vers une clé i18n de message clair. */
export type SendErrorKind =
  | 'auth'      // 401 / 403 : token invalide ou expiré
  | 'network'   // pas de réponse (offline, DNS, CORS, fetch rejeté)
  | 'timeout'   // la requête a dépassé le délai (AbortController)
  | 'server'    // 5xx : le backend a planté
  | 'client'    // 4xx autres : requête refusée (400, 404, 409, 422...)
  | 'unknown';  // tout le reste

export interface SendError {
  /** Catégorie, pour le message clair traduit. */
  kind: SendErrorKind;
  /** Code HTTP si la requête a abouti à une réponse, sinon undefined. */
  httpStatus?: number;
  /**
   * Détail technique brut (ex: `HTTP 401: {"success":false,"error":"Invalid token"}`).
   * Affiché uniquement dans le panneau repliable. Jamais traduit.
   */
  technical: string;
}

/** Marqueur jeté par `sendResultsToCallback` quand la réponse a un statut HTTP. */
export class HttpSendError extends Error {
  constructor(public readonly httpStatus: number, public readonly bodyText: string) {
    super(`HTTP ${httpStatus}: ${bodyText}`);
    this.name = 'HttpSendError';
  }
}

/**
 * Classe une erreur (brute, jetée par la couche fetch) en `SendError` exploitable
 * par l'UI. Tolérant : accepte une `HttpSendError`, une `Error` standard
 * (AbortError, TypeError réseau...) ou n'importe quelle valeur.
 */
export function classifySendError(error: unknown): SendError {
  if (error instanceof HttpSendError) {
    return {
      kind: kindFromStatus(error.httpStatus),
      httpStatus: error.httpStatus,
      technical: error.message,
    };
  }

  if (error instanceof Error) {
    // AbortController déclenche une AbortError lors d'un timeout.
    if (error.name === 'AbortError') {
      return { kind: 'timeout', technical: error.message || 'Request aborted (timeout)' };
    }
    // fetch() rejette avec un TypeError quand il n'y a pas de réponse réseau.
    if (error.name === 'TypeError') {
      return { kind: 'network', technical: error.message || 'Network request failed' };
    }
    return { kind: 'unknown', technical: error.message || String(error) };
  }

  return { kind: 'unknown', technical: String(error) };
}

function kindFromStatus(status: number): SendErrorKind {
  if (status === 401 || status === 403) return 'auth';
  if (status >= 500) return 'server';
  if (status >= 400) return 'client';
  return 'unknown';
}

/** Clé i18n du message clair correspondant à un `kind`. */
export function sendErrorMessageKey(kind: SendErrorKind): string {
  return `errors.send.${kind}`;
}

/**
 * Clé i18n du message clair pour le questionnaire post-match.
 * Ton distinct de la validation : un retour raté n'est pas un résultat perdu,
 * et l'écran feedback propose un bouton « Réessayer ».
 */
export function feedbackErrorMessageKey(kind: SendErrorKind): string {
  return `errors.feedback.${kind}`;
}
